import React, { FC, PureComponent, useContext, useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import styled, { keyframes, useTheme } from 'styled-components';
import colors from '../../../utils/colors';
import { MiniText, SmallText } from '../../../utils/typography';
import { Column, Row, TextOverflowDiv } from '../../common/layoutUtils';
import DurationInput from '../../common/inputs/duration/DurationInput';
import t from '../../../i18n/t';
import MaterialIcon from '../../common/icons/MaterialIcon';
import { TITLE_MAX_LENGTH } from '../../../utils/constants/task';
import { minutesToDurationInHoursNoNegative } from '../../../utils/filters';
import { Subtask, TaskState, WorkloadTask } from 'shovel-lib/types';
import { ManagePageRanges, ManageSubtasks } from '../../task/manage/partials';
import Popup from 'reactjs-popup';
import { popupContentStyle } from '../../../utils/popupUtils';
import { pageRangesToAdditionalText } from '../../../utils/taskUtils';
import TippyTooltip from '../../common/tooltip/TippyTooltip';
import StartAhead from '../../task/manage/partials/StartAhead';
import TableMenu from '../../common/table/partials/TableMenu';
import { readableTextColor } from '../../../utils/colorUtils';
import { isTouchScreen } from '@utils/screenUtils';
import { CELL_LINE_HEIGHT, CELL_PADDING_Y } from './rowMetrics';
import { expectNewTasks } from '@utils/slideRows';
import { isExpanded, SubtaskView, SubtaskViewContext } from './subtaskView';

type RowProps = {
  openTask?: any;
  value?: any;
  save: (data: any, taskId?: number) => void;
  theme?: any;
} & WorkloadTask;

const CellPopup = ({
  className,
  trigger,
  content,
  contentStyle,
  disabled,
  onOpen,
  onClose
}: {
  className?: string;
  trigger: any;
  content: any;
  contentStyle?: any;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}) => {
  return (
    <Popup
      // The library adds "-content" to the last class: ".cell-popup-content" stays on every cell's popup.
      className={className ? `${className} cell-popup` : 'cell-popup'}
      trigger={trigger}
      arrow={false}
      overlayStyle={{ zIndex: 4 }}
      contentStyle={{ ...popupContentStyle, padding: 5, ...contentStyle, zIndex: 4 }}
      position={'bottom left'}
      disabled={disabled}
      onOpen={onOpen}
      onClose={onClose}
    >
      {close => (content instanceof Function ? content(close) : content)}
    </Popup>
  );
};

const DaysAheadCell: FC<RowProps> = props => {
  const { taskId, save, dueDate } = props;
  const value = props.value || props.daysToStartAhead;

  const update = (e: any) => {
    const daysToStartAhead: any = parseInt(e.target.value);
    if (daysToStartAhead >= 0 && value !== daysToStartAhead) {
      save({ daysToStartAhead }, taskId!);
    }
  };

  return (
    <StartAhead
      workload
      calendarClassName={'workload-due-date'}
      // Lined up with the cell's right edge: opened from the right-hand columns it ran off the window.
      pickerPosition={'bottom-end'}
      dueDate={dueDate}
      startAhead={value}
      save={data => save(data, taskId!)}
      activeComponent={
        <TextOverflowDiv>
          {value} {value! >= 0 && (value === 1 ? t.DAY : t.DAYS)}
        </TextOverflowDiv>
      }
    />
  );
};

const PageRangesCell: FC<RowProps> = ({ pageRanges, taskId, save }) => {
  return (
    <CellPopup
      contentStyle={{ width: 160, paddingBottom: 5 }}
      trigger={<TextOverflowDiv>{pageRangesToAdditionalText(pageRanges) || <SmallText>-</SmallText>}</TextOverflowDiv>}
      content={<ManagePageRanges pageRanges={pageRanges} taskId={taskId} autofocus save={save} />}
    />
  );
};

const TimeDurationCell: FC<RowProps & { fieldName: string; isDisabled?: boolean; tooltip?: string }> = props => {
  const { save, taskId, fieldName, isDisabled, tooltip } = props;
  const value = props.value || props.timeNeed;

  const [inputValue, setInputValue] = useState<number | null | undefined>(null);

  const handleChange = (inputValue?: number) => {
    setInputValue(inputValue);
  };

  const setTimeNeed = (newValue: number = 0) => {
    if (newValue === value) {
      setInputValue(null);
      return;
    }
    save({ [fieldName]: newValue }, taskId);
  };

  const valueText = minutesToDurationInHoursNoNegative(value);
  const el = <TextOverflowDiv isDisabled={isDisabled}>{valueText}</TextOverflowDiv>;
  return (
    <CellPopup
      contentStyle={{ width: '100%', minWidth: 80, padding: 0, display: 'flex' }}
      trigger={tooltip ? <TippyTooltip target={el} content={tooltip} preformattedText /> : el}
      disabled={isDisabled}
      content={close => (
        <TimeNeedInput
          noErrorMargin
          autofocus
          onBlur={setTimeNeed}
          onChange={handleChange}
          value={inputValue !== null ? inputValue : value}
          inline
          onKeyUp={e => {
            if (e.key === 'Enter') {
              $(e.target).blur();
              close();
            }
          }}
          disabled={isDisabled}
        />
      )}
    />
  );
};

// The ring in the course colour before the title: always there, so a task is finished where its name is read (it used to
// hide at the far right, off screen on a 1280 px laptop). Pale course colours are darkened just enough to show.
const MarkCompleteCell: FC<RowProps & {
  moveToTodo: (payload: any) => void;
  completeTask: (taskId: number, onFailed?: () => void, checkbox?: Element) => void;
}> = ({ moveToTodo, completeTask, taskId, state, daysToStartAhead, dueDate, colorHex, title }) => {
  const theme = useTheme();
  const completed = state === TaskState.COMPLETED;
  // Ticked at once on the click; the row's green sweep and its slide out are the celebration (no confetti, Petr).
  const [ticked, setTicked] = useState(false);
  const checked = completed || ticked;
  const label = completed ? t.REVERT_TO_INCOMPLETE : t.MARK_COMPLETE;
  return (
    <TippyTooltip
      hideOnClick
      position={'top'}
      content={label}
      target={
        <CompleteRing
          type={'button'}
          aria-label={`${label}: ${title}`}
          className={`complete-ring${checked ? ' checked' : ''}${ticked && !completed ? ' just-ticked' : ''}`}
          $color={ringColor(colorHex, theme)}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            if (completed) {
              moveToTodo({ taskId, data: { daysAhead: daysToStartAhead, dueDate } });
            } else if (!ticked) {
              setTicked(true);
              completeTask(taskId, () => setTicked(false), event.currentTarget);
            }
          }}
        >
          <MaterialIcon name={'check'} size={13} />
        </CompleteRing>
      }
    />
  );
};

/** The same ring without handlers, for rows drawn ahead of scrolling into view (lightWeightContent). */
const CompleteRingPreview: FC<{ colorHex?: string; completed?: boolean }> = ({ colorHex, completed }) => {
  const theme = useTheme();
  return (
    <CompleteRing as={'span'} className={`complete-ring${completed ? ' checked' : ''}`} $color={ringColor(colorHex, theme)}>
      <MaterialIcon name={'check'} size={13} />
    </CompleteRing>
  );
};

// The select box, light like ClickUp's: a thin grey rounded square that fills with the accent when ticked (the app's
// checkbox icon read heavy next to the complete ring - Petr). A dash when only some of a group is selected.
// It names what a click does on hover (Petr): "Select task", or "Select all" on a group's box. The tooltip is built
// only when the mouse arrives (lazy), as every row has a box.
const SelectBox: FC<{
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  clickData?: any;
  tooltip?: string;
  onChange?: (clickData: any, event: React.MouseEvent) => void;
  onMouseDown?: (event: React.MouseEvent) => void;
}> = ({ checked, indeterminate, disabled, clickData, tooltip, onChange, onMouseDown }) => {
  const box = (
    <SelectBoxButton
      type={'button'}
      role={'checkbox'}
      aria-checked={checked ? true : indeterminate ? 'mixed' : false}
      aria-label={tooltip || t.SELECT}
      className={checked ? 'checked' : indeterminate ? 'mixed' : undefined}
      disabled={disabled}
      onMouseDown={onMouseDown}
      onClick={event => {
        event.stopPropagation();
        if (!disabled && onChange) onChange(clickData, event);
      }}
    >
      {(checked || indeterminate) && <MaterialIcon name={checked ? 'check' : 'remove'} size={13} />}
    </SelectBoxButton>
  );
  return tooltip && !disabled ? <TippyTooltip target={box} content={tooltip} lazy /> : box;
};

// The arrow before the ring that opens or closes a task's subtasks under it, as ClickUp's: there whenever it has some, and
// room kept for it on the others so the rings line up.
const SubtaskChevron: FC<{ id: number; taskId?: number; subtasks?: Subtask[] | null }> = ({ id, taskId, subtasks }) => {
  const view = useContext(SubtaskViewContext);
  if (!view.enabled || !taskId || !subtasks?.length) return <ChevronSpace />;
  const expanded = isExpanded(id, view);
  const label = expanded ? t.COLLAPSE_SUBTASKS : t.EXPAND_TASK_SUBTASKS;
  return (
    <TippyTooltip
      lazy
      hideOnClick
      content={label}
      target={
        <ChevronButton
          type={'button'}
          className={'subtask-chevron'}
          aria-label={label}
          aria-expanded={expanded}
          onClick={() => view.toggleExpanded(id)}
        >
          <MaterialIcon name={expanded ? 'arrow_drop_down' : 'arrow_right'} size={18} />
        </ChevronButton>
      }
    />
  );
};

const ringColor = (colorHex: string | undefined, theme: any) =>
  colorHex && theme?.background ? readableTextColor(colorHex, theme.background, 2.5) : colorHex || colors.gray;

// Rename and "Add a subtask" can be started from the row's ⋯ menu, which lives in another cell: each title cell on screen
// registers how to start them, under its task id.
export const renameHandlers = new Map<number, () => void>();
export const subtaskHandlers = new Map<number, () => void>();

type NameCellProps = RowProps & { error?: string; touched?: boolean; isRepeatTasks?: boolean };

// The title: a click opens the task (it used to start renaming, and only a small hover "Open" pill opened it). The pencil,
// or ⋯ → Rename, edits it right where it is - same font and place, one line, so the row keeps its height. Without a task to
// open (the Repeat dialog's rows) a click renames, as before.
class NameCell extends PureComponent<NameCellProps, { editing: boolean; addingSubtasks: boolean }> {
  // addingSubtasks: the + popup is open. It's kept while it is, as the first subtask added would otherwise take it away
  // (it's only there for tasks without active subtasks) and close the field mid-typing (Petr).
  state = { editing: false, addingSubtasks: false };

  // In the Task List the subtasks are lines under the task, as in ClickUp: the counts open or close them, and "+" adds one
  // there - no popups (Petr). The Repeat dialog's rows keep the popups.
  static contextType = SubtaskViewContext;
  context!: SubtaskView;

  subtaskTriggerRef = React.createRef<HTMLElement>();
  titleRef = React.createRef<HTMLSpanElement>();

  componentDidMount() {
    this.register();
  }

  componentDidUpdate(prevProps: NameCellProps) {
    if (prevProps.taskId !== this.props.taskId) {
      this.unregister(prevProps.taskId);
      this.register();
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.closingTimer);
    this.unregister(this.props.taskId);
  }

  register = () => {
    const { taskId } = this.props;
    if (!taskId) return;
    renameHandlers.set(taskId, this.startEditing);
    subtaskHandlers.set(taskId, this.openSubtasks);
  };

  unregister = (taskId?: number) => {
    if (!taskId) return;
    if (renameHandlers.get(taskId) === this.startEditing) renameHandlers.delete(taskId);
    if (subtaskHandlers.get(taskId) === this.openSubtasks) subtaskHandlers.delete(taskId);
  };

  startEditing = () => this.setState({ editing: true });

  // After the popup has finished closing: let go of it any sooner and it updates itself once gone.
  closingTimer?: number;
  doneAddingSubtasks = () => {
    this.closingTimer = window.setTimeout(() => this.setState({ addingSubtasks: false }), 0);
  };

  // The line to add a subtask under the task - or, without the lines (the Repeat dialog), the subtasks popup, as if its
  // trigger had been clicked ("+", or the active count when there are some).
  openSubtasks = () => {
    if (this.context.enabled && this.props.taskId) {
      this.context.startAdding(this.props.id);
      return;
    }
    this.subtaskTriggerRef.current?.click();
  };

  finishEditing = (name: string | null) => {
    this.setState({ editing: false });
    const trimmed = name?.trim();
    if (trimmed) this.save(trimmed);
  };

  save = name => {
    const value = this.props.value || this.props.title;
    return name !== value && this.props.save({ title: name }, this.props.id!);
  };

  addOrUpdateSubtask = (data, oldData) => {
    const subtaskIndex = this.props.subtasks.findIndex(s => s === oldData);
    const subtasks = [...this.props.subtasks];
    subtasks.splice(subtaskIndex !== -1 ? subtaskIndex : subtasks.length, 1, data);
    this.props.save({ subtasks });
  };

  deleteSubtask = oldData => {
    this.props.save({ subtasks: this.props.subtasks.filter(s => s !== oldData) });
  };

  // The ✎ and + take no room until the row is hovered, so the name uses the whole cell, and they sit at the cell's right
  // end (Petr). Over a name that reaches them, its end fades out under them (data-cut), as the sidebar's icons do over a
  // course's name; with subtask counts in the way, the name gives way instead (data-make-room).
  placeHoverActions = (e: React.SyntheticEvent<HTMLDivElement>) => {
    if (isTouchScreen) return;
    const wrapper = e.currentTarget;
    const title = this.titleRef.current;
    const actions = wrapper.querySelector<HTMLElement>('.title-hover-actions');
    if (!title || !actions) return;
    wrapper.removeAttribute('data-cut');
    wrapper.removeAttribute('data-make-room');
    const iconsLeft = actions.getBoundingClientRect().left;
    const counts = Array.from(wrapper.querySelectorAll<HTMLElement>('.subtask-counts')).pop();
    if (counts && counts.getBoundingClientRect().right > iconsLeft) {
      wrapper.setAttribute('data-make-room', '');
      wrapper.style.setProperty('--hover-actions-width', `${actions.offsetWidth}px`);
      return;
    }
    const under = title.getBoundingClientRect().right - iconsLeft;
    if (under > 0) {
      wrapper.setAttribute('data-cut', '');
      wrapper.style.setProperty('--title-under-actions', `${under}px`);
    }
  };

  // Anywhere in the cell opens the task (Petr): not just the name's own text. The ✎, +, subtask counts and their popups
  // do their own thing.
  openFromCell = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // A click away from an open popup only closes it (it lands on the popup's page-wide overlay, which is in the cell).
    if (e.currentTarget.querySelector('.popup-content') || target.closest('.popup-overlay')) return;
    // .subtask-anchor: the + (and ⋯ → Add a subtask) open the subtasks by clicking it.
    if (target.closest('.title-hover-actions, .subtask-counts, .subtask-anchor, .popup-content, input, button')) return;
    this.props.openTask?.();
  };

  render() {
    const { title, taskId, subtasks = [], error, touched, openTask } = this.props;
    const { editing } = this.state;
    const value = this.props.value || title;
    const active = subtasks.filter(s => !s.completed);
    const completed = subtasks.filter(s => s.completed);
    const hasActive = active.length > 0;
    const hasCompleted = completed.length > 0;
    const opensTask = !!(openTask && taskId);
    const showsLines = !!(this.context.enabled && taskId);
    const expanded = showsLines && isExpanded(this.props.id, this.context);
    const onTitle = opensTask ? openTask : this.startEditing;

    return (
      <NameWrapper
        hasError={error && touched}
        className={opensTask && !editing ? 'opens-task-cell' : undefined}
        onMouseEnter={this.placeHoverActions}
        onFocus={this.placeHoverActions}
        onClick={opensTask && !editing ? this.openFromCell : undefined}
      >
        {/* The + (and ⋯ → Add a subtask) open this; it stays at the cell's start, so the popup opens in one place
            wherever the + sits. */}
        {!editing && !showsLines && (!hasActive || this.state.addingSubtasks) && (
          <CellPopup
            className={'subtasks-popup'}
            onOpen={() => this.setState({ addingSubtasks: true })}
            onClose={this.doneAddingSubtasks}
            contentStyle={{ paddingLeft: 15, width: 250 }}
            content={
              <ManageSubtasks
                taskId={taskId}
                inline
                colorHex={this.props.colorHex}
                subtasks={active}
                autoFocus
                customAddOrUpdate={taskId ? undefined : this.addOrUpdateSubtask}
                customDelete={taskId ? undefined : this.deleteSubtask}
                noScroll
              />
            }
            // The ref on a child: the popup takes the trigger's own ref.
            trigger={
              <SubtaskAnchor className={'subtask-anchor'}>
                <span ref={this.subtaskTriggerRef as React.RefObject<HTMLSpanElement>} />
              </SubtaskAnchor>
            }
          />
        )}
        {editing ? (
          <TitleInput value={value} onDone={this.finishEditing} />
        ) : (
          <TitleBox className={opensTask ? undefined : 'fills'}>
            <TitleText
              ref={this.titleRef}
              title={value}
              className={opensTask ? 'title-text opens-task' : 'title-text'}
              role={'button'}
              tabIndex={0}
              // Opening the task is the cell's (openFromCell); renaming, where a click does that, is the name's own.
              onClick={opensTask ? undefined : onTitle}
              onKeyDown={e => {
                if (e.key === 'Enter') onTitle();
              }}
            >
              {value || <TitlePlaceholder>{t.ADD_NAME}</TitlePlaceholder>}
            </TitleText>
            {(opensTask || !hasActive || showsLines) && (
              <HoverActions className={'title-hover-actions'}>
                {opensTask && (
                  <TippyTooltip
                    content={t.RENAME}
                    hideOnClick
                    target={
                      <TitleHoverButton type={'button'} aria-label={t.RENAME} onClick={this.startEditing}>
                        <MaterialIcon name={'edit'} size={14} outline />
                      </TitleHoverButton>
                    }
                  />
                )}
                {(!hasActive || showsLines) && (
                  <TippyTooltip
                    content={t.ADD_A_SUBTASK}
                    target={
                      // After the click is through, so it doesn't close what it opens (as from the ⋯ menu).
                      <AddSubtask onClick={() => setTimeout(this.openSubtasks, 0)}>
                        <MaterialIcon color={colors.primaryPurple} name={'add_circle_outline'} size={16} />
                      </AddSubtask>
                    }
                  />
                )}
              </HoverActions>
            )}
          </TitleBox>
        )}
        {/* Only while closed: open, the lines under the task say the same (Petr), and its arrow closes them. */}
        {showsLines && !expanded && (hasCompleted || hasActive) && (
          <TippyTooltip
            content={expanded ? t.COLLAPSE_SUBTASKS : t.EXPAND_TASK_SUBTASKS}
            hideOnClick
            target={
              <SubtaskCountsButton
                type={'button'}
                className={'subtask-counts'}
                aria-expanded={expanded}
                aria-label={expanded ? t.COLLAPSE_SUBTASKS : t.EXPAND_TASK_SUBTASKS}
                onClick={() => this.context.toggleExpanded(this.props.id)}
              >
                {hasCompleted && <SubtasksCount color={colors.white}>{completed.length}</SubtasksCount>}
                {hasActive && (
                  <SubtasksCount active color={colors.white}>
                    {active.length}
                  </SubtasksCount>
                )}
              </SubtaskCountsButton>
            }
          />
        )}
        {!showsLines && hasCompleted && (
          <CellPopup
            className={'subtasks-popup'}
            contentStyle={{ paddingLeft: 15, width: 250 }}
            content={
              <ManageSubtasks
                taskId={taskId}
                inline
                colorHex={this.props.colorHex}
                noInput
                subtasks={completed}
                customAddOrUpdate={taskId ? undefined : this.addOrUpdateSubtask}
                customDelete={taskId ? undefined : this.deleteSubtask}
              />
            }
            trigger={
              <Column className={'subtask-counts'}>
                <TippyTooltip
                  content={t.VIEW_COMPLETED_SUBTASKS}
                  target={<SubtasksCount color={colors.white}>{completed.length}</SubtasksCount>}
                />
              </Column>
            }
          />
        )}
        {!showsLines && hasActive && (
          <CellPopup
            className={'subtasks-popup'}
            contentStyle={{ paddingLeft: 15, width: 250 }}
            content={
              <ManageSubtasks
                taskId={taskId}
                inline
                colorHex={this.props.colorHex}
                subtasks={active}
                autoFocus
                customAddOrUpdate={taskId ? undefined : this.addOrUpdateSubtask}
                customDelete={taskId ? undefined : this.deleteSubtask}
                noScroll
              />
            }
            trigger={
              <Column className={'subtask-counts'}>
                <TippyTooltip
                  content={t.VIEW_ACTIVE_SUBTASKS}
                  target={
                    <SubtasksCount
                      ref={this.subtaskTriggerRef as React.RefObject<HTMLSpanElement>}
                      active
                      color={colors.white}
                    >
                      {active.length}
                    </SubtasksCount>
                  }
                />
              </Column>
            }
          />
        )}
      </NameWrapper>
    );
  }
}

// One line in the title's own font and place; Enter or clicking away saves, Esc leaves the name as it was.
const TitleInput: FC<{ value: string; onDone: (name: string | null) => void }> = ({ value, onDone }) => {
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    const input = ref.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, []);
  const finish = (name: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(name);
  };
  return (
    <RenameInput
      ref={ref}
      defaultValue={value}
      maxLength={TITLE_MAX_LENGTH}
      placeholder={t.ADD_NAME}
      aria-label={t.RENAME}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(e.currentTarget.value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(null);
        }
      }}
      onBlur={e => finish(e.currentTarget.value)}
    />
  );
};

// What Duplicate copies: everything but the time already used (added back onto the estimate) and the sessions.
const duplicateTask = (task: WorkloadTask, courses: any[], createTasks: any, resetWorkload: () => void) => {
  expectNewTasks([task.title]);
  const taskCourse = courses.find(c => c.id === task.courseId);
  const tasks = [
    {
      name: task.title,
      details: task.details,
      courseId: task.courseId,
      subtasks: task.subtasks.map(s => ({
        content: s.content,
        completed: s.completed,
        ordinalNumber: s.ordinalNumber
      })),
      taskCategoryId: task.taskCategory?.id,
      readingSourceId: task.reading?.id,
      dueDate: task.dueDate,
      daysToStartAhead: task.daysToStartAhead,
      timeNeed: task.reading ? task.timeNeed : task.timeNeed + task.timeSpent,
      // New ranges for the copy: with their ids, the server moved the original's ranges onto it.
      pageRanges: task.pageRanges?.map(({ from, to, pages }) => ({ from, to, pages })),
      automaticEstimation: !!task.reading
    }
  ];
  resetWorkload();
  createTasks({
    tasks,
    colorHex: taskCourse?.colorHex,
    courseCorrelationId: taskCourse?.correlationId,
    title: taskCourse?.name,
    // Sorted by hand, the copy goes right under its original (Petr); otherwise ties keep it there anyway.
    placeAfterTaskId: task.id
  });
};

// The row's ⋯: everything that isn't worth a button of its own, trash last and apart (it used to sit 2 px from the tick).
// Rename and "Add a subtask" wait for the menu to close, so its closing click doesn't close what they open.
const RowActionsMenu: FC<{
  task: WorkloadTask;
  openTask: () => void;
  duplicate?: () => void;
  moveToTrash: () => void;
}> = ({ task, openTask, duplicate, moveToTrash }) => {
  const after = (action?: () => void) => (key: string, close: Function) => {
    close();
    if (action) setTimeout(action, 0);
  };
  const options = [
    { key: 'open', value: <MenuOption icon={'launch'} label={t.OPEN_TASK} />, onClick: after(openTask) },
    {
      key: 'rename',
      value: <MenuOption icon={'edit'} label={t.RENAME} />,
      onClick: after(() => renameHandlers.get(task.taskId)?.())
    },
    {
      key: 'subtask',
      value: <MenuOption icon={'add_circle_outline'} label={t.ADD_A_SUBTASK} />,
      onClick: after(() => subtaskHandlers.get(task.taskId)?.())
    },
    ...(duplicate
      ? [{ key: 'duplicate', value: <MenuOption icon={'content_copy'} label={t.DUPLICATE} />, onClick: after(duplicate) }]
      : [])
  ];
  return (
    <TableMenu
      className={'row-actions'}
      position={'bottom right'}
      width={'170px'}
      target={(active, open) => (
        <RowActionsButton type={'button'} aria-label={t.MORE_OPTIONS} className={open ? 'open' : undefined}>
          <MaterialIcon name={'more_horiz'} size={18} />
        </RowActionsButton>
      )}
      sections={[
        { options },
        {
          options: [
            {
              key: 'trash',
              value: <MenuOption icon={'delete_outline'} label={t.DELETE_TASK} danger />,
              // At once, not after the menu closes: the row starts fading on the click.
              onClick: (key: string, close: Function) => {
                moveToTrash();
                close();
              }
            }
          ]
        }
      ]}
    />
  );
};

const MenuOption: FC<{ icon: string; label: string; danger?: boolean }> = ({ icon, label, danger }) => (
  <MenuOptionRow danger={danger}>
    <MaterialIcon name={icon} size={16} outline />
    <span>{label}</span>
  </MenuOptionRow>
);

export {
  DaysAheadCell,
  PageRangesCell,
  TimeDurationCell,
  MarkCompleteCell,
  SubtaskChevron,
  CHEVRON_WIDTH,
  CompleteRingPreview,
  SelectBox,
  NameCell,
  RowActionsMenu,
  duplicateTask
};

export const AddSubtask = styled(Column)`
  margin-left: 2px;
  &:hover {
    opacity: 0.75;
  }
  transition: opacity 0.2s ease;
`;

export const NameWrapper = styled(Row)<{ hasError?: any }>`
  position: relative;
  height: 100%;
  min-width: 0;
  padding-right: 6px;
  box-sizing: border-box;
  ${props => props.hasError && `box-shadow: inset 0 0 0 1px ${colors.negative};`};
  &.opens-task-cell {
    cursor: pointer;
  }
  /* The name turns the accent wherever in the cell the pointer is - but not over the ✎, + or counts, which aren't it. */
  &.opens-task-cell:hover:not(:has(.title-hover-actions:hover, .subtask-counts:hover)) .opens-task,
  .opens-task:focus-visible {
    color: ${props => props.theme.primary};
  }
  /* ✎ and +: no room taken until hovered (or focused, or while the + popup is open), and then at the cell's right end;
     see placeHoverActions. On a touch screen they always show, after the name. */
  .title-hover-actions {
    ${!isTouchScreen && 'display: none;'}
  }
  &:hover .title-hover-actions,
  &:focus-within .title-hover-actions,
  &:has(.popup-content) .title-hover-actions {
    display: inline-flex;
    ${!isTouchScreen && 'position: absolute; top: 0; bottom: 0; right: 6px;'}
  }
  &[data-make-room]:is(:hover, :focus-within, :has(.popup-content)) {
    padding-right: calc(var(--hover-actions-width) + 10px);
  }
  &[data-cut]:is(:hover, :focus-within, :has(.popup-content)) .title-text {
    -webkit-mask-image: linear-gradient(
      to right,
      #000 calc(100% - var(--title-under-actions) - 8px),
      transparent calc(100% - var(--title-under-actions))
    );
    mask-image: linear-gradient(
      to right,
      #000 calc(100% - var(--title-under-actions) - 8px),
      transparent calc(100% - var(--title-under-actions))
    );
  }
  .cell-popup-content {
    /* The subtask ticks keep their green (TickRing). */
    *:not(.tick-ring) {
      background-color: transparent !important;
    }
  }
`;

// The name; its ✎ and + sit at the cell's right end (NameWrapper). Where a click renames (the Repeat dialog's rows), the
// name takes the whole cell, so a click anywhere on it starts typing (Petr).
const TitleBox = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  height: 100%;
  &.fills {
    position: relative;
    flex: 1;
    .title-text {
      flex: 1;
    }
  }
`;

const SubtaskAnchor = styled.span`
  align-self: stretch;
  width: 0;
  flex-shrink: 0;
`;

const HoverActions = styled.span`
  align-items: center;
  flex-shrink: 0;
`;

const TitleText = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  padding: ${CELL_PADDING_Y}px 0 ${CELL_PADDING_Y}px 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  outline: none;
  cursor: text;
  &.opens-task {
    cursor: pointer;
    transition: color 0.15s ease;
  }
`;

const TitlePlaceholder = styled.span`
  color: ${props => props.theme.placeholder};
`;

/** The title as drawn ahead of scrolling into view (lightWeightContent): text only, same place. */
export const NamePreview: FC<{ title: string }> = ({ title }) => (
  <NameWrapper>
    <TitleText as={'span'}>{title}</TitleText>
  </NameWrapper>
);

const RenameInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  /* As tall as the name it replaces: where the row's height follows its content (the Repeat dialog), a shorter field
     shrank the row and nudged the text and the rows below (Petr). */
  min-height: ${CELL_LINE_HEIGHT + 2 * CELL_PADDING_Y}px;
  box-sizing: border-box;
  margin: 0;
  padding: ${CELL_PADDING_Y}px 0 ${CELL_PADDING_Y}px 4px;
  font: inherit;
  color: inherit;
  border: none;
  outline: none;
  border-radius: 0;
  background: transparent;
  box-shadow: inset 0 -1.5px 0 ${props => props.theme.primary};
  &::placeholder {
    color: ${props => props.theme.placeholder};
  }
`;

// The counts as one button that opens or closes the subtasks under the task.
const SubtaskCountsButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin: 0 0 0 2px;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  &:hover {
    background: color-mix(in srgb, ${props => props.theme.textColor} 14%, transparent);
  }
`;

const CHEVRON_WIDTH = 18;

const ChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${CHEVRON_WIDTH}px;
  height: 18px;
  margin: 0 2px 0 0;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${props => props.theme.captures};
  &:hover {
    color: ${props => props.theme.textStrongColor};
    background: color-mix(in srgb, ${props => props.theme.textColor} 14%, transparent);
  }
`;

const ChevronSpace = styled.span`
  flex-shrink: 0;
  width: ${CHEVRON_WIDTH}px;
  margin-right: 2px;
`;

const TitleHoverButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  margin-left: 4px;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${props => props.theme.textColor};
  transition: color 0.15s ease, background-color 0.15s ease;
  &:hover {
    color: ${props => props.theme.primary};
    background: color-mix(in srgb, ${props => props.theme.primary} 12%, transparent);
  }
`;

const RowActionsButton = styled.button`
  display: inline-flex;
  padding: 2px;
  border: none;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  color: ${props => props.theme.textColor};
  transition: color 0.15s ease, background-color 0.15s ease;
  &:hover,
  &.open {
    color: ${props => props.theme.textStrongColor};
    background: color-mix(in srgb, ${props => props.theme.textColor} 14%, transparent);
  }
`;

const MenuOptionRow = styled.span<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  ${props => props.danger && `color: ${colors.negative};`};
`;

const SubtasksCount = styled(MiniText)<{ active?: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  display: inline-block;
  margin-left: 2px;
  &:last-of-type:not(:first-of-type) {
    margin-left: 5px;
  }
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: unset;
  background: ${props => (props.active ? colors.gray : colors.positive)};
`;

const TimeNeedInput = styled(DurationInput)`
  flex: 1;
  * {
    font-size: 12px !important;
    line-height: 1.5 !important;
    background-color: transparent !important;
  }
  input {
    box-shadow: none !important;
  }
`;

const tickPop = keyframes`
  0% {
    transform: scale(0.6);
  }
  60% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const CompleteRing = styled.button<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid ${props => props.$color};
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  i {
    color: transparent;
    transition: color 0.15s ease;
  }
  &:hover {
    border-color: ${colors.positive};
    background: color-mix(in srgb, ${colors.positive} 16%, transparent);
    i {
      color: ${colors.positive};
    }
  }
  &.checked {
    border-color: ${colors.positive};
    background: ${colors.positive};
    i {
      color: ${colors.white};
    }
    &:hover {
      background: color-mix(in srgb, ${colors.positive} 75%, transparent);
    }
  }
  &.just-ticked {
    animation: ${tickPop} 0.3s ease-out;
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: 2px;
  }
`;

const SelectBoxButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1.5px solid ${props => `color-mix(in srgb, ${props.theme.textColor} 35%, ${props.theme.background})`};
  background: ${props => props.theme.background};
  cursor: pointer;
  i {
    color: ${colors.white} !important;
  }
  &:hover {
    border-color: ${props => `color-mix(in srgb, ${props.theme.textColor} 65%, ${props.theme.background})`};
  }
  &.checked,
  &.mixed {
    background: ${props => props.theme.primary};
    border-color: ${props => props.theme.primary};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: 2px;
  }
`;
